import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Palette,
  Upload,
  Image as ImageIcon,
  Trash2,
  RotateCcw,
  Save,
  Loader2,
  Info,
  Mail,
  Package as PackageIcon,
  Eye,
  AlertTriangle,
  CheckCircle2,
  Paintbrush,
  Download,
  Building2,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

// Relative luminance per WCAG 2.1
function _lum(hex) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16) / 255);
  const t = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * t(r) + 0.7152 * t(g) + 0.0722 * t(b);
}

function contrastRatio(fg, bg) {
  try {
    const L1 = _lum(fg);
    const L2 = _lum(bg);
    const [hi, lo] = L1 > L2 ? [L1, L2] : [L2, L1];
    return (hi + 0.05) / (lo + 0.05);
  } catch (e) {
    return 1;
  }
}

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

/* ------------------------------------------------------------------ */
/*  Asset uploader                                                     */
/* ------------------------------------------------------------------ */

function AssetUploader({ kind, label, recommendedDims, currentAsset, assetPreviewUrl, fallbackUrl, onUpload, onDelete, uploading, testIdPrefix }) {
  const fileRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large — max 10 MB");
      return;
    }
    onUpload(kind, file);
  }, [kind, onUpload]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const currentSrc = assetPreviewUrl || fallbackUrl;

  return (
    <div
      className={`rounded-md border-2 border-dashed p-4 transition-colors ${
        dragActive ? "border-[#006AF2] bg-[#006AF2]/5" : "border-slate-300 dark:border-slate-600"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDrop}
      data-testid={`${testIdPrefix}-dropzone`}
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-[120px] h-[72px] rounded border bg-slate-50 dark:bg-slate-900 flex items-center justify-center overflow-hidden">
          {currentSrc ? (
            <img alt={`${kind} preview`} src={currentSrc} className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="h-6 w-6 text-slate-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="font-medium text-sm">{label}</p>
              <p className="text-xs text-muted-foreground">
                Recommended: {recommendedDims}. PNG / JPEG / SVG / WEBP · max 10 MB.
              </p>
            </div>
            {currentAsset?.file_id && (
              <Badge variant="outline" data-testid={`${testIdPrefix}-badge`}>
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Custom
              </Badge>
            )}
          </div>
          {currentAsset?.file_id && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {currentAsset.filename || `${kind}.bin`} · {fmtBytes(currentAsset.size)}
              {currentAsset.width && currentAsset.height ? ` · ${currentAsset.width}×${currentAsset.height}` : ""}
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid={`${testIdPrefix}-input`}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid={`${testIdPrefix}-upload-btn`}
            >
              {uploading ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Upload className="h-3 w-3 mr-2" />}
              {currentAsset?.file_id ? "Replace" : "Upload"}
            </Button>
            {currentAsset?.file_id && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(kind)}
                data-testid={`${testIdPrefix}-delete-btn`}
              >
                <Trash2 className="h-3 w-3 mr-2" />
                Use default
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main card                                                          */
/* ------------------------------------------------------------------ */

export default function BrandingCard() {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [branding, setBranding] = useState(null);
  const [form, setForm] = useState({
    company_name: "",
    support_email: "",
    support_url: "",
    primary_color: "#006AF2",
    accent_color: "#00D4E4",
    email_footer_html: "",
    email_banner_enabled: true,
    psadt_strings_override_json: "{}",
  });
  const [stringsError, setStringsError] = useState("");

  const authedAxios = useCallback(async () => {
    const token = await getAccessToken?.();
    // CRITICAL: This component creates its own axios instance via
    // ``axios.create()``, which does **not** inherit the global request
    // interceptor that lives on the default axios instance (see
    // ``App.js`` — that interceptor is what normally attaches the
    // ``X-ALICE-Tenant-ID`` header from localStorage so the backend's
    // TenantMiddleware can resolve which ALICE tenant the user is
    // currently acting in). Without it, every branding request (GET,
    // PUT, POST /assets, DELETE /assets/{kind}) falls back to the JWT
    // ``tid`` claim on the backend — i.e. the user's Azure AD tenant id,
    // not the ALICE tenant they have selected — which causes per-tenant
    // branding to leak between ALICE tenants that share an Azure AD
    // tenant or for users with cross-tenant access.
    //
    // We mirror the global interceptor here so this isolated axios
    // instance behaves identically.
    const selectedTenantId =
      typeof window !== "undefined"
        ? localStorage.getItem("alice_selected_tenant_id")
        : null;
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (selectedTenantId) headers["X-ALICE-Tenant-ID"] = selectedTenantId;
    return axios.create({
      baseURL: API,
      headers,
    });
  }, [getAccessToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const http = await authedAxios();
      const { data } = await http.get("/branding");
      setBranding(data);
      setForm({
        company_name: data.company_name || "",
        support_email: data.support_email || "",
        support_url: data.support_url || "",
        primary_color: data.primary_color || "#006AF2",
        accent_color: data.accent_color || "#00D4E4",
        email_footer_html: data.email_footer_html || "",
        email_banner_enabled: data.email_banner_enabled !== false, // default ON
        psadt_strings_override_json: JSON.stringify(data.psadt_strings_override || {}, null, 2),
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load branding");
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => { load(); }, [load]);

  /* Derived state ---------------------------------------------------- */
  const logoPreviewUrl = branding?.logo?.file_id
    ? `${API}/branding/assets/${branding.logo.file_id}?tenant_id=${branding.tenant_id}`
    : null;
  const bannerPreviewUrl = branding?.banner?.file_id
    ? `${API}/branding/assets/${branding.banner.file_id}?tenant_id=${branding.tenant_id}`
    : null;

  const headerContrast = contrastRatio("#ffffff", form.primary_color);
  const contrastBad = headerContrast < 4.5;

  /* Actions ---------------------------------------------------------- */
  const save = async () => {
    // Validate
    if (!HEX_RE.test(form.primary_color) || !HEX_RE.test(form.accent_color)) {
      toast.error("Colours must be hex (#RRGGBB)");
      return;
    }
    let parsedStrings = {};
    if (showAdvanced && form.psadt_strings_override_json.trim()) {
      try {
        parsedStrings = JSON.parse(form.psadt_strings_override_json);
        if (typeof parsedStrings !== "object" || Array.isArray(parsedStrings)) throw new Error("Must be an object");
        setStringsError("");
      } catch (e) {
        setStringsError(`Invalid JSON: ${e.message}`);
        toast.error("PSADT strings override is not valid JSON");
        return;
      }
    }
    setSaving(true);
    try {
      const http = await authedAxios();
      const { data } = await http.put("/branding", {
        company_name: form.company_name.trim(),
        support_email: form.support_email.trim(),
        support_url: form.support_url.trim(),
        primary_color: form.primary_color,
        accent_color: form.accent_color,
        email_footer_html: form.email_footer_html,
        email_banner_enabled: form.email_banner_enabled,
        psadt_strings_override: parsedStrings,
      });
      setBranding(data);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save branding");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset branding to ALICE defaults? This removes your logo, banner and custom strings.")) return;
    setResetting(true);
    try {
      const http = await authedAxios();
      await http.post("/branding/reset");
      toast.success("Branding reset");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset");
    } finally {
      setResetting(false);
    }
  };

  const uploadAsset = async (kind, file) => {
    setUploadingKind(kind);
    try {
      const http = await authedAxios();
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await http.post("/branding/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "logo" ? "Logo" : "Banner"} uploaded`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to upload ${kind}`);
    } finally {
      setUploadingKind(null);
    }
  };

  const deleteAsset = async (kind) => {
    try {
      const http = await authedAxios();
      await http.delete(`/branding/assets/${kind}`);
      toast.success(`${kind === "logo" ? "Logo" : "Banner"} reverted to default`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to delete ${kind}`);
    }
  };

  const previewEmail = async () => {
    setPreviewing(true);
    try {
      const http = await authedAxios();
      const { data } = await http.post("/branding/preview-email", {});
      setPreviewHtml(data.html || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  const downloadPreviewPackage = async () => {
    try {
      const http = await authedAxios();
      const res = await http.post("/branding/preview-package", {}, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "branded-psadt-preview.zip";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Preview package downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download preview package");
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                             */
  /* ------------------------------------------------------------------ */

  if (loading) {
    return (
      <Card data-testid="branding-card-loading">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading branding…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="branding-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-['Chivo']">
          <Paintbrush className="h-5 w-5" />
          Branding
          {branding?.is_default && (
            <Badge variant="secondary" className="ml-2">Using defaults</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Replace the ALICE defaults with your organisation's identity. Branding is applied automatically to every new
          PSADT package you build and every notification email you send. Existing packages are rebranded on their next
          auto-update.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* --- Section 1: Company identity ------------------------------- */}
        <section>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
            <Building2 className="h-4 w-4" /> Company identity
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="branding-company-name">Company name</Label>
              <Input
                id="branding-company-name"
                placeholder="Acme Corp"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                data-testid="branding-company-name-input"
              />
              <p className="text-xs text-muted-foreground">Shown in every PSADT dialog title and email header.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="branding-support-email">Support email</Label>
              <Input
                id="branding-support-email"
                type="email"
                placeholder="itsupport@acme.com"
                value={form.support_email}
                onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                data-testid="branding-support-email-input"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="branding-support-url">Support URL</Label>
              <Input
                id="branding-support-url"
                type="url"
                placeholder="https://help.acme.com"
                value={form.support_url}
                onChange={(e) => setForm({ ...form, support_url: e.target.value })}
                data-testid="branding-support-url-input"
              />
            </div>
          </div>
        </section>

        <Separator />

        {/* --- Section 2: Logo & banner ---------------------------------- */}
        <section>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
            <ImageIcon className="h-4 w-4" /> Logo & banner
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <AssetUploader
              kind="logo"
              label="Logo (AppIcon)"
              recommendedDims="512×512 square"
              currentAsset={branding?.logo}
              assetPreviewUrl={logoPreviewUrl}
              fallbackUrl={branding?.default_logo_url}
              onUpload={uploadAsset}
              onDelete={deleteAsset}
              uploading={uploadingKind === "logo"}
              testIdPrefix="branding-logo"
            />
            <AssetUploader
              kind="banner"
              label="Banner (Classic)"
              recommendedDims="800×150 landscape"
              currentAsset={branding?.banner}
              assetPreviewUrl={bannerPreviewUrl}
              fallbackUrl={branding?.default_banner_url}
              onUpload={uploadAsset}
              onDelete={deleteAsset}
              uploading={uploadingKind === "banner"}
              testIdPrefix="branding-banner"
            />
          </div>

          {/* Email banner rendering toggle -------------------------------- */}
          {/*
             * Outlook rendering safety valve. Some tenants (New Outlook,
             * Outlook on the Web) report that the email body renders BLANK
             * with content only appearing when the user clicks Reply — a
             * classic symptom of the mail renderer choking on part of the
             * banner or its surrounding CSS. Turning this OFF emits every
             * notification email with NO banner image at all, which
             * reliably resolves the symptom while keeping the tenant
             * branding (colours, header, footer, support links) intact.
             */}
          <div
            className="mt-4 flex items-start justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/40 p-3"
            data-testid="branding-email-banner-toggle-row"
          >
            <div className="min-w-0 pr-2">
              <Label
                htmlFor="branding-email-banner-toggle"
                className="text-sm font-medium cursor-pointer"
              >
                Show banner image in emails
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Turn this off if recipients using New Outlook or Outlook on the Web see a
                blank email body (content appears only after clicking Reply). Emails will
                still use your company name, colours, and footer — just without the top
                banner image.
              </p>
            </div>
            <Switch
              id="branding-email-banner-toggle"
              checked={form.email_banner_enabled}
              onCheckedChange={(checked) =>
                setForm({ ...form, email_banner_enabled: checked })
              }
              data-testid="branding-email-banner-toggle"
              aria-label="Show banner image in emails"
            />
          </div>
        </section>

        <Separator />

        {/* --- Section 3: Colours ---------------------------------------- */}
        <section>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
            <Palette className="h-4 w-4" /> Colours
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="branding-primary-color">Primary</Label>
              <div className="flex items-center gap-2">
                <input
                  id="branding-primary-color"
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  className="h-9 w-12 rounded border cursor-pointer"
                  data-testid="branding-primary-color-picker"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                  placeholder="#0047BB"
                  className="flex-1 font-mono"
                  data-testid="branding-primary-color-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="branding-accent-color">Accent</Label>
              <div className="flex items-center gap-2">
                <input
                  id="branding-accent-color"
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  className="h-9 w-12 rounded border cursor-pointer"
                  data-testid="branding-accent-color-picker"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                  placeholder="#FFC107"
                  className="flex-1 font-mono"
                  data-testid="branding-accent-color-input"
                />
              </div>
            </div>
          </div>
          {contrastBad && (
            <Alert className="mt-3 border-amber-400 bg-amber-50 dark:bg-amber-950/40">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800 dark:text-amber-300">Low contrast</AlertTitle>
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                White text on your primary colour has a contrast ratio of {headerContrast.toFixed(2)}:1 (WCAG AA requires ≥4.5). Consider a darker primary or the email title may be hard to read.
              </AlertDescription>
            </Alert>
          )}
        </section>

        <Separator />

        {/* --- Section 4: Live preview ----------------------------------- */}
        <section>
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-3">
            <Eye className="h-4 w-4" /> Live preview
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Email header preview */}
            <div className="rounded border overflow-hidden" data-testid="branding-email-preview">
              <div
                className="text-white text-center py-6 px-4"
                style={{ background: `linear-gradient(135deg, ${form.accent_color}, ${form.primary_color})` }}
              >
                {logoPreviewUrl || branding?.default_logo_url ? (
                  <img
                    alt={form.company_name || "Company logo"}
                    src={logoPreviewUrl || branding?.default_logo_url}
                    style={{ maxHeight: 40, display: "inline-block" }}
                  />
                ) : (
                  <div className="font-bold text-xl tracking-wide">{form.company_name || "ALICE"}</div>
                )}
                <div className="text-sm font-semibold mt-2">Example notification</div>
              </div>
              <div className="p-4 text-xs text-muted-foreground bg-white dark:bg-slate-950">
                Email header preview — exactly how every deployment, approval and digest notification will appear.
              </div>
            </div>

            {/* PSADT dialog preview */}
            <div className="rounded border bg-white dark:bg-slate-900" data-testid="branding-psadt-preview">
              <div
                className="h-[64px] flex items-center justify-center text-white font-semibold"
                style={{
                  backgroundImage: bannerPreviewUrl || branding?.default_banner_url
                    ? `url(${bannerPreviewUrl || branding?.default_banner_url})`
                    : `linear-gradient(135deg, ${form.accent_color}, ${form.primary_color})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {!bannerPreviewUrl && !branding?.default_banner_url && (form.company_name || "ALICE")}
              </div>
              <div className="p-4 space-y-2 text-sm">
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {form.company_name || "ALICE"} — Installing an application…
                </p>
                <p className="text-xs text-muted-foreground">
                  PSADT v4 Classic dialog preview. The CompanyName shown here is injected into every dialog title
                  at packaging time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* --- Section 5: Advanced (collapsible) ------------------------- */}
        <section>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            data-testid="branding-advanced-toggle"
          >
            {showAdvanced ? "▼" : "▶"} Advanced
          </button>
          {showAdvanced && (
            <div className="mt-4 space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Advanced options for enterprises. Leave blank to use the ALICE defaults.
                </AlertDescription>
              </Alert>
              <div className="space-y-1.5">
                <Label htmlFor="branding-footer-html">Email footer HTML</Label>
                <textarea
                  id="branding-footer-html"
                  rows={4}
                  value={form.email_footer_html}
                  onChange={(e) => setForm({ ...form, email_footer_html: e.target.value })}
                  placeholder='<p style="margin:0;">Your custom footer text…</p>'
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                  data-testid="branding-footer-html-textarea"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="branding-psadt-strings">PSADT strings override (JSON object)</Label>
                <textarea
                  id="branding-psadt-strings"
                  rows={6}
                  value={form.psadt_strings_override_json}
                  onChange={(e) => setForm({ ...form, psadt_strings_override_json: e.target.value })}
                  placeholder='{"WelcomeMessage": "Custom welcome…"}'
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
                  data-testid="branding-psadt-strings-textarea"
                />
                <p className="text-xs text-muted-foreground">
                  Maps to keys in <code>Strings/strings.psd1</code>. Only top-level single-quoted string keys are supported.
                </p>
                {stringsError && <p className="text-xs text-destructive">{stringsError}</p>}
              </div>
            </div>
          )}
        </section>

        {/* --- Email preview modal -------------------------------------- */}
        {previewHtml && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="branding-preview-modal">
            <div className="alice-popup bg-white dark:bg-slate-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold">Email preview</h3>
                <Button size="sm" variant="ghost" onClick={() => setPreviewHtml("")} data-testid="branding-preview-close">
                  Close
                </Button>
              </div>
              <iframe
                title="Email preview"
                srcDoc={previewHtml}
                className="flex-1 w-full bg-white"
                sandbox=""
              />
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-wrap items-center gap-2">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="branding-save-btn"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save branding
        </Button>
        <Button
          variant="outline"
          onClick={previewEmail}
          disabled={previewing}
          data-testid="branding-preview-email-btn"
        >
          {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
          Preview email
        </Button>
        <Button
          variant="outline"
          onClick={downloadPreviewPackage}
          data-testid="branding-preview-package-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Download branded PSADT preview
        </Button>
        <div className="flex-1" />
        <Button
          variant="ghost"
          onClick={resetAll}
          disabled={resetting}
          data-testid="branding-reset-btn"
        >
          {resetting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
          Reset to defaults
        </Button>
      </CardFooter>
    </Card>
  );
}
